import { Icon } from "@circle-react-shared/Icon";

import { t } from "@/i18n-js/instance";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";

interface LineItemProps {
  amountWithCurrency: string;
  description: string;
  discountPercentage?: string | null;
  displayIntervalText?: string;
  displayName: string;
  featuresToDisplay?: string[];
  isDoubleUpgrade?: boolean;
  isFirstLineItem?: boolean;
  isFreeTier?: boolean;
  mhCurrentUsage?: number;
  mhMaximumTierUsage?: number;
}

export const LineItem = ({
  displayName,
  amountWithCurrency,
  discountPercentage = null,
  displayIntervalText,
  description,
  isDoubleUpgrade = false,
  isFirstLineItem = false,
  featuresToDisplay = [],
  mhCurrentUsage,
  mhMaximumTierUsage,
  isFreeTier = false,
}: LineItemProps) => (
  <div
    className={classNames({
      "border-primary border-b pb-4": isFirstLineItem,
      "pt-4": isDoubleUpgrade && !isFirstLineItem,
    })}
  >
    <div className="flex flex-row items-start justify-between gap-4">
      <div className="min-w-0">
        <div>
          <div className="flex items-center gap-2">
            <Typography.LabelMd weight="semibold">
              {displayName}
            </Typography.LabelMd>
            {isFreeTier && (
              <Badge
                label={t(
                  "pricing_plans.upgrade_modal.email_hub_line_item.free_tier",
                )}
                variant="secondary"
              />
            )}
            {discountPercentage && (
              <Badge
                inverted
                square
                label={discountPercentage}
                variant="secondary"
              />
            )}
          </div>
        </div>

        <div className="mt-2 flex items-baseline gap-1">
          {mhMaximumTierUsage && (
            <Typography.LabelSm weight="bold">
              {t("pricing_plans.upgrade_modal.email_hub_line_item.contacts", {
                count: mhMaximumTierUsage,
              })}
            </Typography.LabelSm>
          )}
          {mhCurrentUsage && (
            <Typography.LabelXs>
              {t(
                "pricing_plans.upgrade_modal.email_hub_line_item.currently_in_use",
                {
                  count: mhCurrentUsage,
                },
              )}
            </Typography.LabelXs>
          )}
        </div>

        <div className="mt-1 max-w-xs leading-4">
          <Typography.LabelXs color="text-default">
            {description}
          </Typography.LabelXs>
        </div>
        {isDoubleUpgrade && (
          <ul className="mt-2 flex flex-col items-baseline gap-1">
            {featuresToDisplay.map(feature => (
              <li key={feature} className="mb-1 flex items-center gap-2">
                <Icon type="check" size={16} className="!h-4 !w-4" />
                <Typography.LabelXs weight="semibold" color="text-default">
                  {feature}
                </Typography.LabelXs>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
        <Typography.LabelMd weight="semibold">
          {amountWithCurrency}
        </Typography.LabelMd>
        {displayIntervalText && (
          <Typography.BodyMd as="span" color="text-default">
            {displayIntervalText}
          </Typography.BodyMd>
        )}
      </div>
    </div>
  </div>
);
